import { useEffect, useRef } from "react";
import { clearMochiPointer, emitMochiSignal, type MoodKind } from "./signals";
import { useAssistantConversation } from "../../modules/assistant/state/AssistantConversationProvider";

/** Pure mapping — conversation status (+ how many assistant tokens have landed)
 *  → a mood. `respondingAfterTokens` distinguishes "thinking" (nothing yet)
 *  from "responding" (tokens streaming). */
export function moodForConversationStatus(status: string, assistantContentLen: number): MoodKind {
  if (status === "error") return "error";
  if (status === "awaiting_confirmation") return "confirmation";
  if (status === "streaming") return assistantContentLen > 0 ? "responding" : "thinking";
  return "idle";
}

const SUSTAINED_REASSERT_MS = 750;

/** Keeps a mood alive while its source is still active, by re-asserting it on
 *  an interval shorter than signals.ts's 1500ms idle cooldown. `desired ===
 *  null` means "resting" — nothing is emitted and the cooldown (already
 *  armed by the last real assertion) is left to return the mood to idle. */
export function useSustainedMood(desired: MoodKind | null): void {
  useEffect(() => {
    if (!desired) return; // resting → let the cooldown return to idle
    emitMochiSignal({ kind: desired });
    const id = setInterval(() => emitMochiSignal({ kind: desired }), SUSTAINED_REASSERT_MS);
    return () => clearInterval(id);
  }, [desired]);
}

export function useAssistantMoodPublisher(): void {
  const chat = useAssistantConversation();
  const lastAssistantLen = chat.messages.filter((m) => m.role === "assistant").slice(-1)[0]?.content.length ?? 0;
  const mood = moodForConversationStatus(chat.status, lastAssistantLen);
  useSustainedMood(mood === "idle" ? null : mood);

  // Transient "done" celebratory hop: fires once on a genuine
  // streaming → idle transition with no error, never on idle-at-rest or
  // mount. Kept out of useSustainedMood since "done" must not cling.
  const prevStatusRef = useRef(chat.status);
  useEffect(() => {
    const prevStatus = prevStatusRef.current;
    prevStatusRef.current = chat.status;
    if (prevStatus === "streaming" && chat.status === "idle" && !chat.errorMessage) {
      emitMochiSignal({ kind: "done" });
    }
  }, [chat.status, chat.errorMessage]);
}

export function useVoiceMoodPublisher(recorderStatus: string): void {
  const mood =
    recorderStatus === "recording" ? "listening" :
    recorderStatus === "transcribing" ? "transcribing" :
    null;
  useSustainedMood(mood);
}

export function useAmbientPublisher(): void {
  useEffect(() => {
    let raf = 0;
    let noMoveTimer: ReturnType<typeof setTimeout> | undefined;
    const onMove = (e: PointerEvent) => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() =>
        emitMochiSignal({ kind: "look-at", at: { x: e.clientX, y: e.clientY } }),
      );
      // Once the pointer has been still for a while, clear the stale
      // position so the rig's idle/sleep phase (lookaround/sleepy) can
      // advance again — otherwise `at` lingers forever and the rig treats
      // the pointer as perpetually active.
      if (noMoveTimer) clearTimeout(noMoveTimer);
      noMoveTimer = setTimeout(() => clearMochiPointer(), 2500);
    };
    window.addEventListener("pointermove", onMove, { passive: true });
    return () => {
      window.removeEventListener("pointermove", onMove);
      cancelAnimationFrame(raf);
      if (noMoveTimer) clearTimeout(noMoveTimer);
    };
  }, []);
}

/** Mounted only inside Homework (see HomeWorkModule). Listens for office
 *  realtime events on the shared window channel the office provider emits. */
export function useOfficeMoodPublisher(): void {
  useEffect(() => {
    const onEvent = () => emitMochiSignal({ kind: "office-attention" });
    window.addEventListener("weco:office:attention", onEvent);
    return () => window.removeEventListener("weco:office:attention", onEvent);
  }, []);
}
